import {ControlledBrowserHistory} from '@raison/navigation';
import {HttpClient, UploadClient} from '@raison/network-facade';
import {doNothing} from '@raison/toolbox';
import {FetchQueryOptions, QueryClient, QueryObserver} from '@tanstack/react-query';
import {Atom} from 'jotai';
import {paymentMethodPost, withdrawMethodPost} from '~/processes/fiat-operations/api';
import * as api from '~/root/api';

// There's a problem similar to this issue  https://github.com/TanStack/query/issues/3045
// when component subscribed to a query unmounts - it triggers cancellation that cancels calls initiated by queryClient.fetchQuery.
// To ensure (in theory) that fetchQuery is not canceled from external reasons it must have an observer subscribed to it.
const requireQueryCompletion = async <TResultData, TData = TResultData>(
    queryClient: QueryClient,
    options: FetchQueryOptions<TResultData, any, TData, any[]>
) => {
    const unsubscribe = new QueryObserver(queryClient, options).subscribe(doNothing);
    try {
        return await queryClient.fetchQuery(options);
    } finally {
        unsubscribe();
    }
};

export interface AppServicesRequirements {
    queryClient: QueryClient;
    history: ControlledBrowserHistory;
    getAtom: <V>(atom: Atom<V>) => V;
    httpClient: HttpClient;
    httpClientPublic: HttpClient;
    uploadClient: UploadClient;
}

export const createStoreServices = ({
    queryClient,
    getAtom,
    uploadClient,
    httpClient,
    httpClientPublic,
}: AppServicesRequirements) => {
    return {
        upload: uploadClient,
        http: httpClient,
        httpPublic: httpClientPublic,
        fetchUserInfo: () => requireQueryCompletion(queryClient, getAtom(api.userInfo.options$)),
        get2FA: getAtom(api.get2FA),
        fetchDynamicRates: getAtom(api.fetchDynamicRates),
        paymentMethodPost: getAtom(paymentMethodPost),
        withdrawMethodPost: getAtom(withdrawMethodPost),
    };
};
